// Le modèle commun des champs : colonnes du TableNode, champs de la Database
// et des custom nodes. Une colonne EST un champ.
//
// Partagé par Convex et le frontend (même parti pris que `convex/lib/tableOps`).
// Chaque type de champ vit dans son module (`kinds/*.ts`) ; ce fichier ne
// porte que les formes communes. Cf. la spec « Champs : catalogue et
// architecture » sur le canvas.

import type { z } from "zod";
import type { checkboxSettings } from "./kinds/checkbox";
import type { createdTimeSettings, updatedTimeSettings } from "./kinds/systemTime";
import type { DateValue, dateSettings } from "./kinds/date";
import type { emailSettings } from "./kinds/email";
import type { FileRef, fileSettings } from "./kinds/file";
import type { nodeSettings } from "./kinds/node";
import type { relationSettings } from "./kinds/relation";
import type { numberSettings } from "./kinds/number";
import type { phoneSettings } from "./kinds/phone";
import type { RichTextValue, richTextSettings } from "./kinds/richText";
import type {
  multiSelectSettings,
  selectSettings,
  statusSettings,
} from "./kinds/choice";
import type { textSettings } from "./kinds/text";
import type { UrlValue, urlSettings } from "./kinds/url";

/** Les types de la v1. L'ordre est celui du menu « type de champ ». */
export const FIELD_TYPES = [
  "text",
  "rich_text",
  "number",
  "checkbox",
  "select",
  "multi_select",
  "status",
  "date",
  "url",
  "email",
  "phone",
  "file",
  "node",
  "relation",
  "created_time",
  "updated_time",
] as const;

export type FieldType = (typeof FIELD_TYPES)[number];

/** Forme STOCKÉE d'une valeur non vide. Vide = `null`, pour tous les types. */
export type FieldValueMap = {
  text: string;
  rich_text: RichTextValue;
  number: number;
  checkbox: boolean;
  // Ids d'options, jamais leurs noms : renommer une option est gratuit.
  select: string;
  multi_select: string[];
  status: string;
  date: DateValue;
  url: UrlValue;
  email: string;
  // E.164 : "+33612345678".
  phone: string;
  file: FileRef[];
  // Ids de nodeDatas.
  node: string[];
  // Ids des nodeDatas des lignes liées, dans la Database cible.
  relation: string[];
  // Calculés : instants en ms, lus sur le nodeData, jamais stockés.
  created_time: number;
  updated_time: number;
};

export type FieldValue<T extends FieldType = FieldType> = FieldValueMap[T];

export type FieldSettingsMap = {
  text: z.infer<typeof textSettings>;
  rich_text: z.infer<typeof richTextSettings>;
  number: z.infer<typeof numberSettings>;
  checkbox: z.infer<typeof checkboxSettings>;
  select: z.infer<typeof selectSettings>;
  multi_select: z.infer<typeof multiSelectSettings>;
  status: z.infer<typeof statusSettings>;
  date: z.infer<typeof dateSettings>;
  url: z.infer<typeof urlSettings>;
  email: z.infer<typeof emailSettings>;
  phone: z.infer<typeof phoneSettings>;
  file: z.infer<typeof fileSettings>;
  node: z.infer<typeof nodeSettings>;
  relation: z.infer<typeof relationSettings>;
  created_time: z.infer<typeof createdTimeSettings>;
  updated_time: z.infer<typeof updatedTimeSettings>;
};

export type FieldSettings<T extends FieldType = FieldType> =
  FieldSettingsMap[T];

/**
 * La définition d'un champ. `id` est stable : jamais réutilisé ni modifié,
 * c'est la clé des valeurs (`values[field.id]`), pour que renommer soit
 * gratuit.
 */
export type FieldDef<T extends FieldType = FieldType> = {
  [K in T]: {
    id: string;
    name: string;
    type: K;
    // Aide au survol, et consigne pour le LLM (Nolë, Jev, Clairic, workflows).
    description?: string;
    required?: boolean;
    default?: FieldValueMap[K];
    settings: FieldSettingsMap[K];
  };
}[T];

/** Ce qu'une conversion d'entrée brute a besoin de savoir du monde. */
export type CoerceCtx = {
  // Instant de référence des dates relatives (« demain »). Toujours passé :
  // un workflow qui écrit en différé ne doit pas lire l'horloge du serveur.
  now: number;
  // Fuseau IANA dans lequel s'entendent « demain » ou « 14 h ».
  timeZone: string;
  // Locale de la saisie : sépare 1,5 (fr) de 1,500 (en), 05/10 de 10/05.
  locale: string;
};

/**
 * `display` : pour l'œil (abréviations, dates relatives, symboles).
 * `plain` : un texte stable et sans perte, relisible par `coerce` — pivot de
 * conversion, texte indexé, texte donné au LLM, export CSV.
 */
export type FormatStyle = "display" | "plain";

export type FormatCtx = {
  locale: string;
  timeZone: string;
  now: number;
  style: FormatStyle;
  // Titre d'un nodeData référencé (type `node`) ; à défaut, l'id.
  resolveNodeTitle?: (nodeDataId: string) => string | undefined;
};

export type CoerceResult<T extends FieldType> =
  | { ok: true; value: FieldValueMap[T] | null }
  | {
      ok: false;
      reason: string;
      // Options inconnues d'un select : la couche d'écriture peut les créer
      // si le champ l'autorise (`allowCreate`), puis rejouer la conversion.
      unknownOptions?: string[];
    };

/** Ce qu'on sait d'une ligne ou d'un node hors de ses valeurs. */
export type RowMeta = {
  creationTime: number;
  updatedAt: number;
};
